import { Fragment } from 'react';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import {
    Dialog,
    type GetProps,
    isWeb,
    VisuallyHidden,
    XStack,
    YStack,
} from 'tamagui';

import { CONTENT_MAX_WIDTH, isNonMobileWeb } from '../../constants';
import { BackButton, BackButtonIconType } from '../buttons/BackButton';
import { CloseButton } from '../buttons/CloseButton';
import type { SmoothieSheetProps } from '../sheet/Sheet';
import { BodyLargeRegular } from '../text/Typography';

const DialogOverlay = ({ ...props }: GetProps<typeof Dialog.Overlay>) => (
    <Dialog.Overlay
        backgroundColor={isNonMobileWeb ? '$gray800' : '$gray1100'}
        animation={isNonMobileWeb ? 'quick' : 'lazy'}
        animateOnly={['opacity']}
        enterStyle={{ opacity: 0 }}
        exitStyle={{ opacity: 0 }}
        opacity={0.5}
        zIndex={1}
        {...props}
    />
);

// In the case of modal={false}, we have to do a bit of manual
// work here to hook up the overlay and to position these elements
const DialogOverlayFullscreen = ({
    ...props
}: GetProps<typeof Dialog.Overlay>) => (
    <DialogOverlay
        style={{ position: isWeb ? 'fixed' : 'absolute' }}
        forceMount
        {...props}
    />
);

const DialogContent = ({
    children,
    ...props
}: GetProps<typeof Dialog.Content>) => (
    <Dialog.Content
        zIndex={10}
        backgroundColor="$white"
        borderWidth={1}
        borderColor="$gray200"
        animation="slideScreen"
        animateOnly={['opacity', 'transform', 'translateY']}
        enterStyle={{
            y: 10,
            opacity: 0,
            scale: 0.98,
        }}
        exitStyle={{
            y: 5,
            opacity: 0,
            scale: 0.98,
        }}
        width={isNonMobileWeb ? CONTENT_MAX_WIDTH : undefined}
        borderRadius={isNonMobileWeb ? 40 : undefined}
        {...props}
    >
        <VisuallyHidden>
            <Dialog.Title />
        </VisuallyHidden>
        {children}
    </Dialog.Content>
);

// In the case of modal={false}, we have to do a bit of manual
// work here to position these elements
const DialogContentCentered = ({
    ...props
}: GetProps<typeof Dialog.Content>) => (
    <DialogContent
        style={{
            position: isWeb ? 'fixed' : 'absolute',
            top: '50%',
            left: '50%',
            translate: '-50% -50%',
        }}
        {...props}
    />
);

// In the case of modal={false}, we have to do a bit of manual
// work here to position these elements
const DialogContentFullscreen = ({
    ...props
}: GetProps<typeof Dialog.Content>) => (
    <DialogContent
        style={{
            position: isWeb ? 'fixed' : 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
        }}
        {...props}
    />
);

export interface SimpleModalProps extends Pick<
    SmoothieSheetProps,
    | 'children'
    | 'isOpen'
    | 'onClose'
    | 'renderInAppRoot'
    | 'dismissOnOverlayPress'
    | 'withPadding'
> {
    hasCloseButton?: boolean;
    borderRadius?: number | string;
    dialogProps?: GetProps<typeof Dialog.Content>;
}

export const SimpleModal = ({
    isOpen,
    onClose,
    children,
    dismissOnOverlayPress = true,
    hasCloseButton = false,
    renderInAppRoot = true,
    withPadding = true,
    borderRadius,
    dialogProps,
}: SimpleModalProps) => {
    const onOpenChange = (isOpen: boolean) => {
        isOpen && onClose();
    };

    const DialogPortalComponent = renderInAppRoot ? Dialog.Portal : Fragment;
    const DialogOverlayComponent = renderInAppRoot
        ? DialogOverlay
        : DialogOverlayFullscreen;
    const DialogContentComponent = renderInAppRoot
        ? DialogContent
        : DialogContentCentered;

    return (
        isOpen && (
            <Dialog
                open={isOpen}
                onOpenChange={onOpenChange}
                modal={renderInAppRoot}
            >
                <DialogPortalComponent>
                    <DialogOverlayComponent
                        key="overlay"
                        onPress={dismissOnOverlayPress ? onClose : undefined}
                    />
                    <DialogContentComponent
                        key="content"
                        padding={withPadding ? 16 : 0}
                        borderWidth={1}
                        maxHeight="90vh"
                        {...(borderRadius !== undefined && { borderRadius })}
                        {...dialogProps}
                    >
                        {hasCloseButton && (
                            <XStack
                                position="absolute"
                                top={withPadding ? 20 : 4}
                                right={withPadding ? 20 : 4}
                                zIndex={10}
                            >
                                <CloseButton onPress={onClose} />
                            </XStack>
                        )}
                        {children}
                    </DialogContentComponent>
                </DialogPortalComponent>
            </Dialog>
        )
    );
};

interface PanelModalProps extends Pick<
    SmoothieSheetProps,
    'children' | 'isOpen' | 'onClose'
> {
    contentProps?: GetProps<typeof Dialog.Content>;
    hasOverlay?: boolean;
}

export const PanelModal = ({
    isOpen,
    onClose,
    children,
    contentProps,
    hasOverlay = true,
}: PanelModalProps) => {
    const onOpenChange = (isOpen: boolean) => {
        isOpen && onClose();
    };

    if (!isOpen) {
        return null;
    }

    return (
        <Dialog
            open={isOpen}
            onOpenChange={onOpenChange}
            disableRemoveScroll={!hasOverlay} // enable page scrolling if no backdrop overlay
            modal={false}
        >
            {/* modal={false} because when true (it defaults to true) it was causing problems with the 
                order of nested providers (the PortalProvider in the TamaguiProvider is required to
                be high in our tree, but then its higher than other providers required for
                content within the panel -- e.g. SendbirdProvider for messaging
            */}
            {/* The docs say we should include a `Dialog.Portal` wrapper here, but doing so is causing
                the dialog to always be rendered in app root (which modal=false is supposed to prevent)
                Excluding it achieves what we want, which is for the Sendbird message dropdown menus to
                not be layered behind this Dialog.
                <Dialog.Portal
                pointerEvents={hasOverlay ? 'auto' : 'none'} // Seems like it shouldn't be necessary, but it is
                zIndex={1}
            > */}
            {/* I'm not sure why the onPress and forceMount are necessary, but it seems to be caused
                    by having modal={false} above */}
            {hasOverlay && (
                <DialogOverlay key="overlay" onPress={onClose} forceMount />
            )}
            <DialogContent
                key="content"
                position="absolute"
                animation="quick"
                enterStyle={{ opacity: 0, y: -30 }}
                pointerEvents="auto"
                {...contentProps}
            >
                <XStack
                    display="flex"
                    justifyContent="flex-end"
                    position="relative"
                    top={-75}
                    right={-15}
                    height={0}
                >
                    <CloseButton
                        onPress={onClose}
                        padding={12}
                        borderRadius={50}
                        borderWidth={1}
                        borderColor="$gray200"
                        background="gray"
                        {...(isWeb && {
                            boxShadow: 'rgba(0, 0, 0, 0.086) 0px 4px 8px',
                        })}
                    />
                </XStack>
                {children}
            </DialogContent>
            {/* </Dialog.Portal> */}
        </Dialog>
    );
};

interface ResponsiveScreenModalProps {
    isOpen: boolean;
    onOpenChange: (isOpen: boolean) => void;
    children: React.ReactNode;
    title?: string;
    hasHeader?: boolean;
    withPadding?: boolean;
    renderInAppRoot?: boolean;
}

export const ResponsiveScreenModal = ({
    isOpen,
    onOpenChange,
    children,
    title,
    hasHeader = true,
    withPadding = true,
    renderInAppRoot = true,
}: ResponsiveScreenModalProps) => {
    const { top, bottom } = useSafeAreaInsets();
    const basePadding = withPadding ? 24 : 0;

    const DialogPortalComponent = renderInAppRoot ? Dialog.Portal : Fragment;
    const DialogOverlayComponent = renderInAppRoot
        ? DialogOverlay
        : DialogOverlayFullscreen;
    const DialogContentComponent = isNonMobileWeb
        ? DialogContent
        : DialogContentFullscreen;

    if (!isOpen) {
        return null;
    }

    return (
        <Dialog
            open={isOpen}
            onOpenChange={onOpenChange}
            modal={renderInAppRoot}
        >
            <DialogPortalComponent>
                <DialogOverlayComponent key="overlay" />
                <DialogContentComponent
                    key="content"
                    unstyled={!isNonMobileWeb} // unstyled for mobile so it can look full screen
                    maxHeight={isNonMobileWeb ? '90vh' : '100%'}
                    maxWidth={isNonMobileWeb ? '90vw' : '100%'}
                    width={isNonMobileWeb ? 'auto' : '100%'}
                    paddingHorizontal={basePadding}
                    paddingTop={
                        hasHeader ? Math.max(top, basePadding) : basePadding
                    }
                    paddingBottom={Math.max(bottom, basePadding)}
                    enterStyle={
                        isNonMobileWeb
                            ? {
                                  y: 10,
                                  opacity: 0,
                                  scale: 0.95,
                              }
                            : { x: 400 }
                    }
                    exitStyle={
                        isNonMobileWeb
                            ? {
                                  y: 5,
                                  opacity: 0,
                                  scale: 0.95,
                              }
                            : { x: 400 }
                    }
                    style={{
                        borderRadius: isNonMobileWeb ? 40 : undefined,
                    }}
                >
                    <YStack
                        height="100%"
                        $sm={{
                            height: 'auto',
                            maxHeight: '100%',
                        }}
                    >
                        {hasHeader && (
                            <XStack
                                paddingTop={12}
                                paddingVertical={8}
                                $sm={{ paddingTop: 0 }}
                                alignItems="center"
                                justifyContent="space-between"
                            >
                                <XStack width={48}>
                                    <XStack
                                        display="flex"
                                        $sm={{ display: 'none' }}
                                    >
                                        <BackButton
                                            background="gray"
                                            iconType={
                                                BackButtonIconType.ARROW_LEFT
                                            }
                                            customBackFn={() => {
                                                onOpenChange(false);
                                            }}
                                        />
                                    </XStack>
                                </XStack>
                                <XStack
                                    flex={1}
                                    alignItems="center"
                                    justifyContent="center"
                                >
                                    {title != null && title !== '' && (
                                        <BodyLargeRegular
                                            width="100%"
                                            textAlign="center"
                                        >
                                            {title}
                                        </BodyLargeRegular>
                                    )}
                                </XStack>
                                <XStack width={48} justifyContent="flex-end">
                                    <XStack
                                        display="none"
                                        $sm={{ display: 'flex' }}
                                    >
                                        <CloseButton
                                            background="gray"
                                            onPress={() => {
                                                onOpenChange(false);
                                            }}
                                        />
                                    </XStack>
                                </XStack>
                            </XStack>
                        )}
                        {children}
                    </YStack>
                </DialogContentComponent>
            </DialogPortalComponent>
        </Dialog>
    );
};
